import { OrdersFilterSearch } from "../../pages/orders-page/types";
import { getEndOfDay, getStartOfDay, isPastDate } from "../../shared/lib/dates";
import { RentAreaListItem } from "../rent-area/types";

import {
  ChangeOrderFieldsRequest,
  CreateOrderRequest,
  GetOrdersFilter,
  OrderFieldsDto,
  OrderFilesDto,
} from "./api";
import {
  EMPTY_PERMISSIONS,
  ORDER_STATUS_OPTIONS,
  TENANT_VISIBLE_ORDER_STATUSES,
} from "./constants";
import {
  Order,
  OrderListItem,
  OrderRentAreaFilterOption,
  OrderSchemasDto,
  OrdersFilterFormValues,
} from "./types";
import {
  CustomFieldsFilesValues,
  CustomFieldsFormValues,
  orderSettingsFormSchema,
  orderStatusesSchema,
} from "./types-schema";

export const parseOrderSchema = ({
  currentStatusCode,
  role = "Renter",
  ...data
}: Partial<OrderSchemasDto> & {
  currentStatusCode?: string;
  role?: string;
} = {}) => {
  {
    if (!data.fieldsSchema && !data.statusesSchema) return EMPTY_SCHEMA;

    try {
      const fieldsSchema = orderSettingsFormSchema.safeParse(
        JSON.parse(data.fieldsSchema || ""),
      );
      const statusesSchema = orderStatusesSchema.safeParse(
        JSON.parse(data.statusesSchema || ""),
      );
      const currentStatus = statusesSchema.success
        ? statusesSchema.data.statuses.find(
            (status) => status.statusCode === currentStatusCode,
          )
        : undefined;
      const permissions =
        currentStatus?.rolePermissions.find((item) => item.role === role)
          ?.permission || EMPTY_PERMISSIONS;
      const transitions =
        currentStatus?.roleTransitions.find((item) => item.role === role)
          ?.transitions || [];
      const isValid = fieldsSchema.success && statusesSchema.success;

      return {
        fields: fieldsSchema.success ? fieldsSchema.data.fields : [],
        layoutDetails: fieldsSchema.success
          ? fieldsSchema.data.layoutDetails
          : [],
        layoutProcess: fieldsSchema.success
          ? fieldsSchema.data.layoutProcess
          : [],
        permissions,
        transitions,
        plannedCompletionMinutes: currentStatus?.plannedCompletionMinutes,
        isValid,
      };
    } catch (error) {
      return EMPTY_SCHEMA;
    }
  }
};

const EMPTY_SCHEMA = {
  fields: [],
  layoutDetails: [],
  layoutProcess: [],
  permissions: EMPTY_PERMISSIONS,
  transitions: [],
  plannedCompletionMinutes: undefined,
  isValid: false,
};

export const parseOrderFormValuesToUpdateRequest = (
  orderId: string,
  fieldsValues: CustomFieldsFormValues,
  filesValues: CustomFieldsFilesValues,
): ChangeOrderFieldsRequest => {
  return {
    orderId,
    fields: parseOrderFieldsToDto(fieldsValues),
    fileFields: parseOrderFilesToDto(filesValues),
  };
};

export const parseOrderFormValuesToCreateRequest = (
  subtypeViewId: string,
  rentAreaId: string,
  fieldsValues: CustomFieldsFormValues,
  filesValues: CustomFieldsFilesValues,
): CreateOrderRequest => {
  return {
    subtypeViewId,
    rentAreaId,
    fields: parseOrderFieldsToDto(fieldsValues),
    fileFields: parseOrderFilesToDto(filesValues),
  };
};

export const parseOrderFieldsToDto = (
  fields: CustomFieldsFormValues,
): OrderFieldsDto[] =>
  Object.entries(fields).map(([k, values]) => ({
    fieldId: parseInt(k, 10),
    values,
  }));

export const parseOrderFilesToDto = (
  files: CustomFieldsFilesValues,
): OrderFilesDto[] =>
  Object.entries(files)
    .filter(([, files]) => files.length > 0)
    .map(([k, files]) => ({
      fieldId: parseInt(k, 10),
      files,
    }));

export const parseFilterSearchToRequestDto = (
  f: OrdersFilterSearch,
): GetOrdersFilter => {
  return {
    statuses: f.statuses?.length
      ? f.statuses
      : [...TENANT_VISIBLE_ORDER_STATUSES],
    rentAreaIds: f.rentAreas?.length ? f.rentAreas : undefined,
    typeIds: f.orderTypes?.length
      ? f.orderTypes.map((ot) => parseInt(ot, 10))
      : undefined,
    plannedDateFilter: {
      start: f.slaFrom ? getStartOfDay(f.slaFrom).toISOString() : undefined,
      end: f.slaTo ? getEndOfDay(f.slaTo).toISOString() : undefined,
    },
    createdDateFilter: {
      start: f.createdFrom
        ? getStartOfDay(f.createdFrom).toISOString()
        : undefined,
      end: f.createdTo ? getEndOfDay(f.createdTo).toISOString() : undefined,
    },
  };
};

export const parseFilterFormValuesToFilterSearch = (
  values: OrdersFilterFormValues,
): OrdersFilterSearch => {
  return {
    statuses: values.statuses?.map(({ value }) => value),
    rentAreas: values.rentAreas?.map(({ rentAreaId }) => rentAreaId),
    orderTypes: values.orderTypes?.map(({ id }) => id.toString()),
    slaFrom: values.slaFrom,
    slaTo: values.slaTo,
    createdFrom: values.createdFrom,
    createdTo: values.createdTo,
  };
};

export const parseFilterSearchToFormValues = ({
  filter: f,
  rentAreaOptions = [],
  orderTypeOptions = [],
}: {
  filter: OrdersFilterSearch;
  rentAreaOptions?: NonNullable<OrdersFilterFormValues["rentAreas"]>;
  orderTypeOptions?: NonNullable<OrdersFilterFormValues["orderTypes"]>;
}): OrdersFilterFormValues => {
  return {
    statuses:
      f.statuses
        ?.map((s) => ORDER_STATUS_OPTIONS.find((o) => o.value === s))
        .filter(Boolean) || [],
    rentAreas:
      f.rentAreas
        ?.map((rentAreaId) =>
          rentAreaOptions.find((option) => option.rentAreaId === rentAreaId),
        )
        .filter(Boolean) || [],
    orderTypes:
      f.orderTypes
        ?.map((orderTypeId) =>
          orderTypeOptions.find(
            (option) => option.id.toString() === orderTypeId,
          ),
        )
        .filter(Boolean) || [],
    slaFrom: f.slaFrom || "",
    slaTo: f.slaTo || "",
    createdFrom: f.createdFrom || "",
    createdTo: f.createdTo || "",
  };
};

type RentAreaFilterOptionSource = Pick<
  RentAreaListItem,
  "project" | "rawAddress" | "rentAreaId" | "rentAreaName" | "unit"
>;

export const mapRentAreaToFilterOption = ({
  project,
  rawAddress,
  rentAreaId,
  rentAreaName,
  unit,
}: RentAreaFilterOptionSource): OrderRentAreaFilterOption => ({
  rentAreaId,
  unit: unit || rentAreaName || rawAddress || rentAreaId,
  projectName: project.name || null,
});

type OrderSlaNotificationSource = Pick<
  Order | OrderListItem,
  "deadlineNotificationDate" | "plannedCompletionDate"
>;

export const hasReachedOrderSla = (
  order: OrderSlaNotificationSource,
): boolean => {
  return Boolean(
    order.deadlineNotificationDate &&
      order.plannedCompletionDate &&
      isPastDate(order.deadlineNotificationDate),
  );
};

type OrderExecutionOverdueSource = Pick<
  Order | OrderListItem,
  "plannedCompletionDate" | "completionDate" | "isCompleted"
> &
  Partial<Pick<OrderListItem, "actualCompletionDate">>;

type OrderTypeLabelSource = Pick<OrderListItem, "typeName" | "subtypeName"> & {
  subtypeView?: {
    name?: string | null;
  } | null;
};

export const getOrderSubtypeName = (order: OrderTypeLabelSource): string =>
  order.subtypeView?.name?.trim() || order.subtypeName?.trim() || "";

export const getOrderTypeLabels = (order: OrderTypeLabelSource) => {
  const typeName = order.typeName?.trim() || "";
  const subtypeName = getOrderSubtypeName(order);
  const primary = typeName || subtypeName || "-";
  const secondary =
    typeName && subtypeName ? `${typeName} - ${subtypeName}` : "";

  return {
    primary,
    secondary,
    full: secondary || primary,
  };
};

export const formatOrderNumber = (number: number) =>
  `№${number.toString().padStart(7, "0")}`;

export const isOrderExecutionOverdue = (
  order: OrderExecutionOverdueSource,
): boolean => {
  if (!order.plannedCompletionDate) return false;

  const plannedCompletionTime = new Date(order.plannedCompletionDate).getTime();
  const completedAt = order.actualCompletionDate || order.completionDate;

  if (completedAt) {
    return new Date(completedAt).getTime() > plannedCompletionTime;
  }

  if (order.isCompleted) return false;

  return isPastDate(order.plannedCompletionDate);
};
